"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type Theme = "dark" | "light";
type ThemePreference = Theme | "system";

type ThemeContextValue = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
const THEME_KEY = "sa-theme";
const LEGACY_THEME_KEY = "sa_theme";
const THEME_COOKIE = "theme";
const SYSTEM_QUERY = "(prefers-color-scheme: dark)";

function isThemePreference(value: unknown): value is ThemePreference {
  return value === "dark" || value === "light" || value === "system";
}

function readStoredThemePreference(): ThemePreference | null {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem(THEME_KEY);
  if (stored && isThemePreference(stored)) return stored;
  const legacyStored = localStorage.getItem(LEGACY_THEME_KEY);
  if (legacyStored && isThemePreference(legacyStored)) return legacyStored;

  if (typeof document === "undefined") return null;
  const themeCookie = document.cookie.match(new RegExp(`(^|; )${THEME_COOKIE}=([^;]+)`));
  const cookieValue = themeCookie?.[2] ?? null;
  if (cookieValue && isThemePreference(cookieValue)) return cookieValue;

  return null;
}

function resolveSystemTheme(): Theme {
  if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
    return "light";
  }
  return window.matchMedia(SYSTEM_QUERY).matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePref, setThemePref] = useState<ThemePreference>(() => readStoredThemePreference() ?? "system");
  const [systemTheme, setSystemTheme] = useState<Theme>(() => resolveSystemTheme());
  const resolvedTheme: Theme = themePref === "system" ? systemTheme : themePref;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia === "undefined") return;
    const mediaQueryList: MediaQueryList = window.matchMedia(SYSTEM_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
    document.documentElement.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const value = themePref;
    localStorage.setItem(THEME_KEY, value);
    localStorage.setItem(LEGACY_THEME_KEY, value);
    document.cookie = `${THEME_COOKIE}=${value}; path=/; max-age=31536000; samesite=lax`;
  }, [themePref]);

  const toggleTheme = useMemo(() => {
    return () => {
      const nextTheme = (resolvedTheme === "dark" ? "light" : "dark") satisfies Theme;
      setThemePref(nextTheme);
    };
  }, [resolvedTheme]);

  return <ThemeContext.Provider value={{ theme: resolvedTheme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
}
